import React, { useEffect, useState } from "react";
import { useTheme } from "../../theme/useTheme";
import {
  diseaseOptionsClient,
  type DiseaseOption,
} from "../../services/diseaseOptionsClient";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  FormHelperText,
} from "@mui/material";

interface DiseaseSelectProps {
  therapeuticArea: string;
  selectedDisease: string;
  onDiseaseChange: (diseaseId: string) => void;
  /** Optional gene symbol to filter diseases by. Only shows diseases where the gene is present. */
  geneId?: string;
}

export const DiseaseSelect: React.FC<DiseaseSelectProps> = ({
  therapeuticArea,
  selectedDisease,
  onDiseaseChange,
  geneId,
}) => {
  useEffect(() => {
    console.log("DiseaseSelect mounted");
  }, []);

  useEffect(() => {
    console.log("DiseaseSelect prop selectedDisease:", selectedDisease);
  }, [selectedDisease]);
  const themeCtx = useTheme();
  const [diseaseOptions, setDiseaseOptions] = useState<DiseaseOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDiseaseOptions = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const options = await diseaseOptionsClient.getDiseaseOptions({
          therapeuticArea,
          geneId,
        });
        setDiseaseOptions(options);
        console.log("Fetched diseaseOptions:", options, "for geneId:", geneId);
      } catch (err) {
        console.error("Error fetching disease options:", err);
        setError("Failed to load diseases");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDiseaseOptions();
  }, [therapeuticArea, geneId]);

  return (
    <div className="max-w-2xl">
      <FormControl
        fullWidth
        size="small"
        disabled={isLoading}
        error={!!error}
        sx={{
          backgroundColor: themeCtx.mode === "dark" ? "#1a202c" : "#fff",
          borderRadius: 1,
          ".MuiInputLabel-root": {
            color: themeCtx.mode === "dark" ? "#cbd5e1" : undefined,
          },
          ".MuiOutlinedInput-root": {
            backgroundColor: themeCtx.mode === "dark" ? "#1a202c" : "#fff",
            color: themeCtx.mode === "dark" ? "#e5e7eb" : "#111827",
            "& fieldset": {
              borderColor: themeCtx.mode === "dark" ? "#334155" : "#cbd5e1",
            },
          },
          ".MuiSelect-icon": {
            color: themeCtx.mode === "dark" ? "#cbd5e1" : undefined,
          },
        }}
      >
        <InputLabel
          id="disease-select-label"
          className="dark:text-gray-300"
          sx={{
            color: themeCtx.mode === "dark" ? "#ffffff" : undefined,
            "&.MuiInputLabel-root": {
              color: themeCtx.mode === "dark" ? "#ffffff" : undefined,
            },
          }}
        >
          {isLoading ? "Loading diseases..." : "Select a disease"}
        </InputLabel>
        <Select
          labelId="disease-select-label"
          id="disease-select"
          value={selectedDisease}
          label={isLoading ? "Loading diseases..." : "Select a disease"}
          onChange={(e) => {
            const value = e.target.value as string;
            onDiseaseChange(value);
          }}
          disabled={isLoading || !!error}
          className="bg-white dark:bg-gray-900 dark:text-gray-100"
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: themeCtx.mode === "dark" ? "#1a202c" : "#fff",
                color: themeCtx.mode === "dark" ? "#e5e7eb" : "#111827",
                border:
                  themeCtx.mode === "dark"
                    ? "1px solid #334155"
                    : "1px solid #cbd5e1",
                "& .MuiMenuItem-root": {
                  backgroundColor:
                    themeCtx.mode === "dark" ? "#1a202c" : "#fff",
                  color: themeCtx.mode === "dark" ? "#e5e7eb" : "#111827",
                  "&.Mui-selected": {
                    backgroundColor:
                      themeCtx.mode === "dark" ? "#334155" : "#e5e7eb",
                    color: themeCtx.mode === "dark" ? "#fff" : "#111827",
                  },
                  "&:hover": {
                    backgroundColor:
                      themeCtx.mode === "dark" ? "#334155" : "#f3f4f6",
                    color: themeCtx.mode === "dark" ? "#fff" : "#111827",
                  },
                },
              },
            },
          }}
        >
          {isLoading ? (
            <MenuItem value="" disabled>
              <div className="flex items-center gap-2">
                <CircularProgress
                  size={20}
                  sx={{
                    color: themeCtx.mode === "dark" ? "#cbd5e1" : "#111827",
                  }}
                />
                <span
                  style={{
                    color: themeCtx.mode === "dark" ? "#f3f4f6" : "#111827",
                  }}
                >
                  Loading diseases...
                </span>
              </div>
            </MenuItem>
          ) : error ? (
            <MenuItem value="" disabled>
              <span className="text-red-500 dark:text-red-400">
                Error loading diseases
              </span>
            </MenuItem>
          ) : (
            diseaseOptions.map((disease) => (
              <MenuItem key={disease.id} value={disease.id}>
                {disease.label}
              </MenuItem>
            ))
          )}
        </Select>
        {error && (
          <FormHelperText error sx={{ color: "#ef4444" }}>
            {error}
          </FormHelperText>
        )}
      </FormControl>
    </div>
  );
};
